import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2, Users, ChevronDown, ChevronRight, Loader2, UserCircle, Briefcase
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LEVEL_COLORS = {
  DG: "from-red-500 to-red-600",
  DDG: "from-orange-500 to-orange-600",
  CD: "from-amber-500 to-amber-600",
  DIR: "from-yellow-500 to-yellow-600",
  DDIR: "from-lime-500 to-lime-600",
  AD: "from-green-500 to-green-600",
  DAD: "from-teal-500 to-teal-600",
  AST: "from-sky-500 to-sky-600",
};

function TreeNode({ node, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const colorClass = LEVEL_COLORS[node.level_code] || "from-slate-500 to-slate-600";

  return (
    <div className="select-none">
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
        style={{ marginLeft: depth * 20 }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />
        ) : (
          <div className="w-4" />
        )}
        <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${colorClass}`} />
        <span className="text-xs font-mono text-slate-500 min-w-[80px]">{node.post_number}</span>
        <span className="text-sm font-medium text-slate-800">{node.title || node.level_code}</span>
        {node.department && (
          <span className="text-xs text-slate-400 ml-1">— {node.department}</span>
        )}
        {node.occupant && (
          <Badge variant="outline" className="ml-2 text-xs bg-green-50 text-green-700 border-green-200">
            <UserCircle className="w-3 h-3 mr-1" />
            {node.occupant.name}
          </Badge>
        )}
        {!node.occupant && (
          <Badge variant="outline" className="ml-2 text-xs bg-app text-slate-400 border-slate-200">
            Vacant
          </Badge>
        )}
        {hasChildren && (
          <span className="text-xs text-slate-400 ml-auto">{node.children.length} sub-positions</span>
        )}
      </div>
      {expanded && hasChildren && (
        <div className="border-l-2 border-slate-200 ml-6">
          {node.children.map((child, idx) => (
            <TreeNode key={child.post_number || idx} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OurOrganisation() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchOrganogram();
  }, []);

  const fetchOrganogram = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/api/hr/organogram/tree`, {
        headers: getHeaders(), withCredentials: true
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load organogram data");
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Building2 className="w-12 h-12 text-slate-300" />
        <p className="text-slate-500">No organisation data available. Run seed generation from HR Admin.</p>
      </div>
    );
  }

  const totalPositions = data.total_positions || 0;
  const levels = data.levels || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-['Barlow_Condensed'] font-bold text-slate-800 tracking-wide">
          Our Organisation
        </h1>
        <p className="text-sm text-slate-500">Organisation structure — hierarchical organogram view</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{totalPositions.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Total Positions</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{levels.length}</p>
              <p className="text-xs text-slate-500">Organisation Levels</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <UserCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {levels.map(l => l.code).join(" → ")}
              </p>
              <p className="text-xs text-slate-500">Hierarchy Chain</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Level Legend */}
      <div className="flex flex-wrap gap-2">
        {levels.map((level) => (
          <Badge
            key={level.code}
            className={`bg-gradient-to-r ${LEVEL_COLORS[level.code] || "from-slate-500 to-slate-600"} text-white`}
          >
            {level.code} — {level.name}
          </Badge>
        ))}
      </div>

      {/* Tree View */}
      <Card className="bg-white border-slate-200">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-700">Organogram Tree</h2>
        </div>
        <ScrollArea className="max-h-[70vh] p-4">
          {data.tree && data.tree.length > 0 ? (
            data.tree.map((node, idx) => (
              <TreeNode key={node.post_number || idx} node={node} depth={0} />
            ))
          ) : (
            <p className="text-slate-400 text-center py-8">No positions found in tree</p>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}
